import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The fourteenth written page. Everything below comes from the five-part
// full-page capture of digitopia.co/customers/lila-kagit/ sent on 2026-08-28 —
// "tastamam şekilde, aynısını, tıpkısını tasarla" — with the four photographs
// supplied alongside it.
//
// Its shape is the reference's, section for section: the film stays in the
// header (it was already here), the standfirst is the grey band, and then the
// page alternates — two centred sections on the white page stating the
// problem, then three grey panels answering it. The last two panels are the
// two-column shapes that already exist: ebebek's mirror (`photoSide: 'left'`,
// the panel enclosing both columns) and Intel AI's (`photoSide: 'right'` with
// `photoInPanel`), which is what the capture shows — the panel runs the full
// container width with the words in one half and the picture in the other.
//
// The closing quote is not repeated as a `section`: `content/testimonials.ts`
// has held Tarkan Ateşoğlu's words keyed to this slug since the Hall of Fame
// shipped, and `StoryBody` draws that card under the story. That is the
// testimonial template the request asked for, and it needed no new media —
// the request offered a portrait and Lila Kağıt's mark, and both are already
// here: `/logo/lilakagitcard.png` and a 900×900 monochrome cut of the same
// headshot, against the 880×880 supplied, so the files already in the tree are
// the better ones. The reference's wording of the quote is ours to the word.
export const added = story('lila-kagit', 'Lila Kağıt', {
  video: 'gCwIstlrvQQ',
  lede: [
    'By adopting Digitopia’s structured methodology, Lila Kağıt has successfully created a '
    + 'measurable roadmap for digital investments and process improvements.',
  ],
  intro:
    'Lila Kağıt is a leading hygiene paper manufacturer serving both domestic and '
    + 'international markets. The company reaches consumers at more than 80,000 points with '
    + 'its brands Sofia, Maylo, UltraBerrak, and Nua, offering toilet paper, paper towels, '
    + 'tissue paper, handkerchiefs, and napkins.',
  photo: {
    src: '/photo/lila-kagit-paper-products-wall.webp',
    alt: 'Lila Kağıt’s range laid out edge to edge: toilet rolls, kitchen towels, folded '
      + 'napkins and tissue packs in white and pale green, packed into a flat wall of paper.',
  },
  sections: [
    {
      heading: 'The Need: Driving Digital Transformation for Greater Efficiency',
      centred: true,
      body: [
        'As a major player in the hygiene paper industry, Lila Kağıt recognized the growing '
        + 'need to enhance efficiency, streamline processes, and foster digital '
        + 'transformation. The company aimed to eliminate repetitive tasks, leverage '
        + 'real-time data analytics, and optimize business operations to maintain its '
        + 'competitive edge. However, with a rapidly evolving market and increasing global '
        + 'demand, achieving alignment across business units and prioritizing digital '
        + 'investments became critical.',
      ],
    },
    {
      heading: 'The Challenge: Aligning Strategy with Execution',
      centred: true,
      body: [
        'Lila Kağıt faced the challenge of integrating digital transformation into its '
        + 'corporate culture while ensuring that all teams were aligned on priorities. With '
        + 'limited resources and high external dependencies, the company needed a structured '
        + 'approach to assess its current digital capabilities, identify key opportunities, '
        + 'and allocate resources effectively. Managing this shift required strategic '
        + 'alignment between leadership, IT, and operational teams to drive meaningful '
        + 'digital initiatives.',
      ],
      photo: {
        src: '/photo/lila-kagit-paper-mill-reel.webp',
        alt: 'A finished reel of tissue paper hanging from an overhead crane in Lila Kağıt’s '
          + 'mill, above a line of paper machines with a worker in a hard hat beside them.',
      },
    },
    {
      heading: 'Why Digitopia: A Data-Driven Roadmap for Transformation',
      centred: true,
      band: true,
      body: [
        'To navigate this transformation, Lila Kağıt partnered with Digitopia to leverage the '
        + 'Digital Maturity Index (DMI). Unlike generic digitalization strategies, the DMI '
        + 'methodology provided a structured, data-driven assessment of the company’s digital '
        + 'capabilities across six key dimensions: customer, governance, operations, people, '
        + 'innovation, and technology. Through moderated workshops and competitive insights, '
        + 'the company gained a clear understanding of its current standing and formulated a '
        + 'strategic roadmap for digital growth.',
      ],
    },
    {
      heading: 'The Approach: Cross-Functional Collaboration and Measurable Progress',
      band: true,
      body: ['Lila Kağıt implemented a holistic approach to digital transformation by:'],
      items: [
        {
          text: 'Engaging cross-functional teams to evaluate digital maturity and identify '
            + 'key priorities.',
        },
        {
          text: 'Utilizing the DMI assessment to align leadership and departments on a '
            + 'shared vision.',
        },
        {
          text: 'Leveraging data analytics and automation tools to enhance operational '
            + 'efficiency.',
        },
        {
          text: 'Ensuring strategic alignment between IT, business units, and external '
            + 'partners.',
        },
      ],
      photo: {
        src: '/photo/lila-kagit-office-corridor.webp',
        alt: 'A corridor in Lila Kağıt’s offices: glazed meeting rooms along one side, a '
          + 'timber wall on the other carrying the company’s leaf mark planted in living '
          + 'moss above a run of foliage.',
      },
      photoSide: 'left',
    },
    {
      heading: 'The Impact: A More Agile and Data-Driven Organization',
      band: true,
      body: ['By adopting Digitopia’s structured methodology, Lila Kağıt has successfully:'],
      items: [
        { text: 'Established clear accountability across departments for digital initiatives.' },
        {
          text: 'Created a measurable roadmap for digital investments and process '
            + 'improvements.',
        },
        {
          text: 'Increased awareness and engagement in digital transformation across the '
            + 'company.',
        },
        {
          text: 'Strengthened its market position by optimizing operations and driving '
            + 'innovation.',
        },
      ],
      photo: {
        src: '/photo/lila-kagit-production-line.webp',
        alt: 'Finished toilet rolls travelling down converting lines in Lila Kağıt’s plant, '
          + 'with the machine hall and overhead gantries behind them.',
      },
      photoSide: 'right',
      photoInPanel: true,
    },
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/lila-kagit/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — in particular the “more than '
    + '80,000 points” figure and the four brand names in the opening band (Sofia, Maylo, '
    + 'UltraBerrak, Nua), which are the only hard facts on the page. Two things are the '
    + 'reference’s own and were left verbatim rather than silently tidied: the em-dashes '
    + 'around the brand list are set closed up, as digitopia.co sets them, and the sentence '
    + 'that opens the page is repeated as the lead-in to “The Impact” at the foot of it. '
    + 'Where the two grey panels at the foot each show a picture, the capture shows an empty '
    + 'half — the images had not loaded when it was taken — so which of the four photographs '
    + 'sits in which of those two halves is our reading of the file names supplied '
    + '(lila-kagit-3 beside The Approach, Lila-Kagit-4 beside The Impact) and not something '
    + 'the capture proves; the two wide pictures, the product wall and the mill, are visible '
    + 'in it and are placed where it puts them. Two deliberate differences of shape from the '
    + 'reference, each a template-wide decision rather than this page’s: the header eyebrow '
    + 'reads “Digitopia / Customer Success Story” where digitopia.co reads “SUCCESS STORIES”, '
    + 'and this story has no PDF, so there is no “Read the full story” button — it was not '
    + 'among the sixteen documents sent on 2026-08-27. One production note: all four '
    + 'photographs are small originals at the sizes the live site serves — 900×471, 900×600, '
    + '470×600 and 1200×800 — so they are drawn at that width and will look soft on a '
    + 'high-density screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 21
